<template>
  <section id="shuttle-unit-search-section">
    <el-collapse v-model="isOpen">
      <el-collapse-item
        :title="$t('common.search')"
        name="shuttle-unit-search-section"
      >
        <el-form
          id="shuttle-unit-search-section-form"
          class="search-section-form"
          :model="searchFormModel"
          ref="searchForm"
          label-position="right"
          size="mini"
          inline
        >
          <el-row :gutter="10">
            <el-col :span="8">
              <el-form-item :label="$t('outbound.shuttleUnit.dayOfService')">
                <el-date-picker
                  class="el-dropdown-new drop-sp date-dob"
                  @change="handleSearch"
                  v-model="searchFormModel.dayOfService"
                  format="MM/dd/yyyy"
                  type="date"
                  :clearable="false"
                ></el-date-picker>
              </el-form-item>
            </el-col>
            <el-col :span="8">
              <el-form-item :label="$t('outbound.shuttleUnit.receivingWarehouse')">
                <outbound-warehouse-select v-model="searchFormModel.warehouseId" @change="handleSearch" :includeOptionAll="true" />
              </el-form-item>
            </el-col>
            <el-col :span="8">
              <el-form-item :label="$t('outbound.shuttleUnit.outboundWarehouse')">
                <outbound-warehouse-select v-model="searchFormModel.outboundWarehouseId" @change="handleSearch" :includeOptionAll="true" />
              </el-form-item>
            </el-col>
          </el-row>

          <el-row :gutter="10">
            <el-col :span="8">
              <el-form-item :label="$t('outbound.shuttleUnit.shuttled')">
                <el-select v-model="searchFormModel.shuttled" @change="handleSearch">
                  <el-option
                    v-for="item in shuttledOptions"
                    :key="item.value"
                    :label="item.label"
                    :value="item.value"
                  ></el-option>
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :span="8">
              <el-form-item :label="$t('outbound.units.shipper')">
                <autocomplete
                  :model.sync="searchFormModel.shipperAccountId"
                  :shipperAccountId="null"
                  labelFieldLastWithDash="number"
                  popperAppendToBody
                  :url="urlShipper"
                  :selectAction="handleSearch"
                  :enterKeyAction="handleSearch"
                  :clearAction="handleSearch"
                  labelField="name"
                  valueField="id"
                  ref="filter-shipperAccountId"
                  id="searchForm-shipperAccountId"
                />
              </el-form-item>
            </el-col>
            <el-col :span="8">
              <el-form-item :label="$t('outbound.units.consignee')">
                <autocomplete
                  :model.sync="searchFormModel.consigneeAccountId"
                  :shipperAccountId="null"
                  labelFieldLastWithDash="number"
                  popperAppendToBody
                  :url="urlConsignee"
                  :selectAction="handleSearch"
                  :enterKeyAction="handleSearch"
                  :clearAction="handleSearch"
                  labelField="name"
                  valueField="id"
                  ref="filter-consigneeAccountId"
                  id="searchForm-consigneeAccountId"
                />
              </el-form-item>
            </el-col>
          </el-row>
          <el-row :gutter="10">
            <el-col :span="8">
              <el-form-item :label="$t('outbound.shuttleUnit.shuttleNumber')">
                <el-input
                  v-model="searchFormModel.shuttleNumber"
                  v-alphanumeric-validation
                  maxlength="15"
                  @clear="handleSearch"
                  v-on:keyup.enter.native="handleSearch"
                  class="inline-input"
                  clearable
                ></el-input>
              </el-form-item>
            </el-col>
            <el-col :span="8">
              <el-form-item :label="$t('outbound.shuttleUnit.putAwayId')">
                <el-input
                  v-model="searchFormModel.putAwayCode"
                  v-alphanumeric-validation
                  maxlength="15"
                  @clear="handleSearch"
                  v-on:keyup.enter.native="handleSearch"
                  class="inline-input"
                  clearable
                ></el-input>
              </el-form-item>
            </el-col>
            <el-col :span="8">
              <el-form-item :label="$t('outbound.shuttleUnit.status')">
                <el-select
                  v-model="filterStatus"
                  id="searchForm-status"
                  ref="filter-status"
                  @change="handleSearch"
                  >
                  <el-option
                    v-for="option in unitStatusOptions"
                    :key="option.value"
                    :label="option.label"
                    :value="option.value"
                  ></el-option>
                </el-select>
              </el-form-item>
            </el-col>
          </el-row>
          <el-row :gutter="10">
            <el-col :span="24" class="text-align-end">
              <el-tooltip
                :content="$t('common.search')"
                class="item"
                effect="dark"
                placement="top-start"
              >
                <el-button
                  id="shuttle-unit-search-section-form-button"
                  class="button-action-default"
                  @click="handleSearch"
                  icon="el-icon-search"
                  size="mini"
                ></el-button>
              </el-tooltip>
            </el-col>
          </el-row>
        </el-form>
      </el-collapse-item>
    </el-collapse>
  </section>
</template>

<script>
import { mapActions } from 'vuex';
import constants from '@/utils/constants';
import OutboundWarehouseSelect from '@/views/routing/components/OutboundWarehouseSelect';
import Autocomplete from '@/components/Autocomplete';
import apiConstants from '@/utils/apiConstants';
import { SHUTTLE_UNIT_SEARCH } from '@/store/actions';

export default {
  name: 'ShuttleUnitSearchSection',
  components: {
    Autocomplete,
    OutboundWarehouseSelect
  },
  computed: {
    shuttledOptions() {
      return [
        {
          value: null,
          label: this.$t('common.all')
        },
        {
          value: constants.STATUS_BOOLEAN.yes.value,
          label: constants.STATUS_BOOLEAN.yes.label
        },
        {
          value: constants.STATUS_BOOLEAN.no.value,
          label: constants.STATUS_BOOLEAN.no.label
        }
      ];
    },
    unitReceivedStatus() {
      return constants.UNIT.STATUS[2];
    },
    unitArrivedStatus() {
      return constants.UNIT.STATUS[8];
    },
    urlConsignee() {
      return apiConstants.END_POINTS.ACCOUNTS.CONSIGNEE;
    },
    urlShipper() {
      return apiConstants.END_POINTS.ACCOUNTS.SHIPPERS;
    },
    unitStatusOptions() {
      return constants.UNIT.STATUS;
    },
  },
  data() {
    return {
      activeCollapsibleName: ['shuttle-unit-search-section'],
      isOpen: ['shuttle-unit-search-section'],
      searchFormModel: {
        warehouseId: null,
        dayOfService: new Date(),
        outboundWarehouseId: null,
        putAwayCode: null,
        shuttled: constants.STATUS_BOOLEAN.no.value,
        status: null,
        shipperAccountId: null,
        consigneeAccountId: null,
        shuttleNumber: null,
        checkForArrivedShuttle: false,
        page: constants.TABLES.DEFAULT_PAGE,
      },
      filterStatus: constants.UNIT.STATUS[0].value
    };
  },
  methods: {
    ...mapActions({
      search: SHUTTLE_UNIT_SEARCH,
    }),
    handleSearch() {
      this.searchFormModel.status = this.filterStatus;
      this.searchFormModel.checkForArrivedShuttle = false;
      if (this.searchFormModel.status === this.unitArrivedStatus.value) {
        this.searchFormModel.status = this.unitReceivedStatus.value;
        this.searchFormModel.checkForArrivedShuttle = true;
      }

      this.search(this.searchFormModel);
    }
  },
  mounted() {
    this.handleSearch();
  }
};
</script>
